import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Plus, Clock, CheckCircle2, MessageSquare, Loader2, ChevronRight } from "lucide-react";

const STATUS_META = {
  open: { label: "Abierto", cls: "bg-brand-orange-500/20 text-brand-orange-300" },
  in_progress: { label: "En atención", cls: "bg-amber-500/20 text-amber-300" },
  waiting_user: { label: "Esperando tu respuesta", cls: "bg-cyan-500/20 text-cyan-300" },
  resolved: { label: "Resuelto", cls: "bg-emerald-500/20 text-emerald-300" },
  closed: { label: "Cerrado", cls: "bg-gray-500/20 text-gray-400" },
};
const PRIORITY_META = {
  urgent: "bg-red-500/20 text-red-300",
  high: "bg-orange-500/20 text-orange-300",
  normal: "bg-white/10 text-gray-300",
  low: "bg-white/5 text-gray-400",
};

export default function AlumnoSoporte() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/support/tickets");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AlumnoLayout user={user}>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Alumno</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Soporte DCC</h1>
          <p className="mt-1 text-sm text-gray-400">¿Necesitas ayuda? Abre un ticket y un asesor te contactará.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
                data-testid="soporte-new-ticket-btn"
                className="inline-flex items-center gap-2 rounded-md bg-[#F26A21] px-4 py-2 text-sm font-semibold text-[#0B0F14] hover:brightness-110">
          <Plus className="w-4 h-4" /> Nuevo ticket
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="soporte-empty">
          <LifeBuoy className="w-8 h-8 text-gray-500 mx-auto" />
          <p className="mt-3 text-sm text-gray-300">No tienes tickets abiertos.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map(t => (
            <Link key={t.id} to={`/app/alumno/soporte/${t.ticket_number}`}
                  data-testid={`soporte-row-${t.id}`}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/[0.08]">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-gray-500">{t.ticket_number}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 rounded ${PRIORITY_META[t.priority] || ""}`}>{t.priority}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 rounded ${STATUS_META[t.status]?.cls || ""}`}>{STATUS_META[t.status]?.label}</span>
                </div>
                <p className="mt-1 text-sm font-semibold text-white">{t.subject}</p>
                <p className="mt-0.5 text-[11px] text-gray-400 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Abierto {new Date(t.created_at).toLocaleDateString("es-MX")} · Categoría: {t.category}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0 mt-2" />
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateTicketModal onClose={() => setShowCreate(false)}
                                        onCreated={(t) => { setShowCreate(false); nav(`/app/alumno/soporte/${t.ticket_number}`); }} />}
    </AlumnoLayout>
  );
}

function CreateTicketModal({ onClose, onCreated }) {
  const [category, setCategory] = useState("acceso");
  const [priority, setPriority] = useState("normal");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3 || body.trim().length < 10) {
      toast.error("Asunto ≥3 chars · Descripción ≥10 chars.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/support/tickets", {
        category, priority, subject: subject.trim(), body: body.trim(),
      });
      toast.success(`Ticket ${r.data.ticket_number} creado.`);
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            data-testid="soporte-create-modal"
            className="w-full max-w-lg rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[92vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white">Nuevo ticket</h2>
        <div className="mt-4 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-semibold text-gray-300 mb-1">Categoría</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      data-testid="soporte-cat-select"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                <option value="facturacion">Facturación</option>
                <option value="acceso">Acceso / Login</option>
                <option value="contenido">Contenido</option>
                <option value="certificado">Certificado</option>
                <option value="tecnico">Técnico</option>
                <option value="otro">Otro</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-300 mb-1">Prioridad</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                      data-testid="soporte-prio-select"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-300 mb-1">Asunto</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                   data-testid="soporte-subject-input"
                   className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                   placeholder="Resumen breve" required />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-300 mb-1">Describe tu problema</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                      data-testid="soporte-body-input"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                      placeholder="Cuéntanos qué ocurre, pasos para reproducir, capturas si aplica…" required />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-white/20 text-white text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="soporte-submit-btn"
                  className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Enviar
          </button>
        </div>
      </form>
    </div>
  );
}
